import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import {
  InAppNotificationDto,
  CommentNotificationDataDto,
  CategoryUnlockedNotificationDataDto,
} from "../../../../types/in-app-notification.dto";
import { markNotificationRead } from "../../../../store/actions/in-app-notification";
import Icon from "../../../Icon";
import commentIcon from "../../../../assets/sessions/notification-icon.svg";
import unlockIcon from "../../../../assets/icons/padlock-unlock.svg";
import {
  ItemWrapper,
  UnreadDot,
  ReadDotPlaceholder,
  IconWrapper,
  Content,
  Title,
  Body,
  Timestamp,
} from "./styles";

moment.locale("es");

interface ComponentProps {
  notification: InAppNotificationDto;
  onClose: () => void;
}

const NotificationItem = ({ notification, onClose }: ComponentProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isUnread = notification.read_at === null;
  const isCategoryUnlocked = notification.type === "category_unlocked";

  const handleClick = useCallback(() => {
    if (isUnread) {
      dispatch(markNotificationRead({ id: notification.id }));
    }

    if (notification.type === "category_unlocked") {
      const { group_id, category_id } =
        notification.data as CategoryUnlockedNotificationDataDto;
      navigate(`/group/${group_id}/p/c/${category_id}`);
    } else {
      const { group_id, post_id, parent_id, comment_id } =
        notification.data as CommentNotificationDataDto;
      let hash: string;
      if (parent_id) {
        hash = `#thread-${parent_id}`;
      } else if (comment_id) {
        hash = `#comment-${comment_id}`;
      } else {
        hash = "#post-comments";
      }
      navigate(`/group/${group_id}/p/${post_id}${hash}`);
    }

    onClose();
  }, [notification, isUnread, dispatch, navigate, onClose]);

  const icon = isCategoryUnlocked ? unlockIcon : commentIcon;

  return (
    <ItemWrapper isUnread={isUnread} onClick={handleClick}>
      {isUnread ? <UnreadDot /> : <ReadDotPlaceholder />}

      <IconWrapper isUnread={isUnread}>
        <Icon
          icon={icon}
          size="18px"
          color={isUnread ? "#6C5CE7" : "rgba(45, 52, 54, 0.45)"}
        />
      </IconWrapper>

      <Content>
        <Title isUnread={isUnread}>{notification.title}</Title>
        <Body>{notification.body}</Body>
        <Timestamp>{moment(notification.last_activity_at).fromNow()}</Timestamp>
      </Content>
    </ItemWrapper>
  );
};

export default NotificationItem;
